/**
 * AppHeader — Iter158
 *
 * Reusable sticky top-of-page header with the mobile-kebab pattern first
 * proven on the Agency Dashboard (iter157). Solves the recurring "header
 * overflows the iPhone portrait viewport" bug in one place.
 *
 * ── Anatomy (mobile <md) ─────────────────────────────────────────────
 *   ┌─────────────────────────────────────────────────┐
 *   │ [logo] Title (subtitle hidden)    [chips] [⋮]   │
 *   └─────────────────────────────────────────────────┘
 *                                               │
 *                                               ▼
 *                                     ┌───────────────────┐
 *                                     │ mobileMenu (slot) │
 *                                     └───────────────────┘
 *
 * ── Anatomy (desktop ≥md) ────────────────────────────────────────────
 *   ┌───────────────────────────────────────────────────────────────┐
 *   │ [logo] Title / subtitle   [ desktop children slot inline ]    │
 *   └───────────────────────────────────────────────────────────────┘
 *
 * Props
 *   • title, subtitle, testid  — required identity
 *   • logo                     — optional ReactNode (usually <Logo/>)
 *   • children                 — desktop toolbar (rendered under `hidden md:flex`)
 *   • mobileVisible            — compact chips that stay visible on <md
 *                                 (e.g. credits chip, notifications bell)
 *   • mobileMenu               — slot rendered inside the kebab dropdown
 *   • rightGap                 — Tailwind gap size for the actions rail (default gap-2)
 *
 * The header is always `sticky top-0` and uses the same colors/paddings
 * (`px-3 sm:px-6 lg:px-8 py-3 sm:py-4`) verified in iter157 testing.
 */
import { useEffect, useRef, useState } from "react";
import { MoreVertical, Menu } from "lucide-react";
import { useSidebarState } from "./SidebarContext";

export default function AppHeader({
  title,
  subtitle,
  logo,
  testid = "app-header",
  children,
  mobileVisible,
  mobileMenu,
  rightGap = "gap-2",
  zIndex = 30,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside or on Esc.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    window.addEventListener("touchstart", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("touchstart", onClick);
    };
  }, [menuOpen]);

  return (
    <header
      data-testid={testid}
      style={{ zIndex }}
      className="sticky top-0 bg-[var(--al-card-bg)] border-b border-[var(--al-border)] px-3 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between gap-2"
    >
      {/* LEFT — identity block (always visible; truncates on mobile) */}
      <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
        <SidebarHamburger />
        {logo}
        <div className="min-w-0">
          <h1 className="text-[14px] sm:text-[16px] font-semibold text-[var(--al-text)] truncate">
            {title}
          </h1>
          {subtitle && (
            <p className="text-[11.5px] sm:text-[12.5px] text-[var(--al-text-muted)] mt-0.5 truncate hidden sm:block">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {/* DESKTOP toolbar (hidden below md) */}
      {children && (
        <div className={`hidden md:flex items-center ${rightGap} shrink-0`}
             data-testid={`${testid}-desktop-actions`}>
          {children}
        </div>
      )}

      {/* MOBILE toolbar — compact chips + kebab */}
      {(mobileVisible || mobileMenu) && (
        <div className="md:hidden flex items-center gap-2 shrink-0 relative" ref={menuRef}>
          {mobileVisible}
          {mobileMenu && (
            <>
              <button
                type="button"
                data-testid={`${testid}-kebab`}
                aria-expanded={menuOpen}
                aria-label="Open menu"
                onClick={() => setMenuOpen((v) => !v)}
                className="w-9 h-9 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] inline-flex items-center justify-center hover:border-[#C9A24E] transition-colors"
              >
                <MoreVertical className="w-4 h-4" />
              </button>
              {menuOpen && (
                <div
                  data-testid={`${testid}-mobile-menu`}
                  className="absolute right-0 top-11 z-50 w-56 max-w-[calc(100vw-24px)] rounded-xl border border-[var(--al-border)] bg-[var(--al-card-bg)] shadow-2xl p-1.5"
                  onClick={() => setMenuOpen(false)}
                >
                  {mobileMenu}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </header>
  );
}

/**
 * SidebarHamburger — mobile-only button that opens the app's Sidebar
 * drawer. Rendered inside every AppHeader so pages that use it inherit
 * the mobile nav for free.
 */
function SidebarHamburger() {
  const { setMobileOpen } = useSidebarState();
  return (
    <button
      type="button"
      onClick={() => setMobileOpen(true)}
      aria-label="Open navigation"
      data-testid="app-header-sidebar-toggle"
      className="lg:hidden w-9 h-9 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] flex items-center justify-center text-[var(--al-text)] hover:border-[#C9A24E]"
    >
      <Menu className="w-4 h-4" />
    </button>
  );
}
